<template>
  <div
    class="free-input"
    :class="{
      'cursor-not-allowed opacity-50': $attrs.disabled,
    }"
  >
    <label
      v-if="label || Boolean($slots.label)"
      :for="id"
      class="text-text-neutral-tertiary mb-3 block text-xs font-medium"
    >
      <slot name="label"> {{ label }} </slot>
    </label>
    <div
      class="free-input__wrapper flex w-full items-center border border-solid px-4 py-3 duration-150"
      :class="{
        'border-transparent': !hasError && !isFocused,
        'border-background-negative-accent': hasError,
        'border-background-positive-primary': isFocused && !hasError,
        'h-12 rounded-xl': size === 'md',
        'h-[52px] rounded-xl ': size === 'lg',
        'h-[52px] rounded-2xl ': size === '2xl',
      }"
    >
      <div class="block grow">
        <div
          :class="[
            'free-input__input-container',
            {
              'free-input__input-container_has-prepend-slot': Boolean(
                $slots.prepend,
              ),
            },
          ]"
        >
          <div v-if="Boolean($slots.prepend)" class="mr-3">
            <slot name="prepend" />
          </div>
          <input
            v-bind="$attrs"
            ref="inputRef"
            :value="modelValue"
            :class="[
              'free-input__input',
              { 'w-[98%]': isClearable || Boolean($slots.append) },
            ]"
            @input="
              emit(
                'update:modelValue',
                ($event.target as HTMLInputElement).value,
              )
            "
            @focus="isFocused = true"
            @blur="isFocused = false"
          />
        </div>
      </div>
      <Transition name="input-fade">
        <div
          v-if="isClearable && Boolean($slots.clearButton) && isFocused"
          class="text-text-neutral-quaternary mx-1 my-0 cursor-pointer"
          @click="handleClearButtonClick"
        >
          <slot name="clearButton" />
        </div>
      </Transition>
      <div v-if="Boolean($slots.append)" class="mx-2 my-0">
        <slot name="append" v-bind="{ isClearable, handleClearButtonClick }" />
      </div>
    </div>
    <Transition name="input-fade">
      <div v-if="hasError" class="text-text-negative-primary mt-2 text-xs">
        <slot name="errorMessage" />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, useAttrs } from 'vue';
  import { isFunc } from '@aviata/type-guards';
  import { uuid4 as uuid } from '@sentry/utils';

  type InputSize = 'md' | 'lg' | '2xl';

  const props = withDefaults(
    defineProps<{
      modelValue?: string;
      clearable?: boolean;
      label?: string;
      size?: InputSize;
      hasError?: boolean;
    }>(),
    {
      clearable: true,
      modelValue: '',
      label: '',
      hasError: false,
      size: 'md',
    },
  );
  const emit = defineEmits<{
    'update:modelValue': [string];
  }>();

  const attrs = useAttrs();

  const id = uuid();
  const inputRef = ref<HTMLInputElement>();
  const isFocused = ref(false);
  const isClearable = computed(
    () => props.clearable && Boolean(props.modelValue),
  );

  const handleClearButtonClick = () => {
    /** If maska is used, need to use onMaska event to update value */
    if (
      attrs.hasOwnProperty('onMaska') &&
      isFunc(attrs.onMaska) &&
      inputRef.value
    ) {
      inputRef.value.value = '';
      const maskaEvent = {
        target: inputRef.value,
        detail: {
          masked: '',
          unmasked: '',
          completed: false,
        },
      };
      attrs.onMaska(maskaEvent);
    } else {
      emit('update:modelValue', '');
    }
    inputRef.value?.focus();
  };

  defineExpose({
    inputRef,
  });
</script>

<style scoped>
  .free-input ::placeholder {
    @apply text-text-neutral-quaternary;
  }

  .free-input ::placeholder,
  .free-input__input {
    @apply text-base not-italic;
  }

  .free-input__wrapper {
    background-color: var(
      --input-background,
      rgb(var(--situational-lightgrey))
    );
    border-radius: var(--input-border-radius, 0.75rem);
  }

  .free-input__input-container {
    @apply w-full;
  }

  .free-input__input-container_has-prepend-slot {
    @apply flex items-center;
  }

  .free-input__input-container_has-prepend-slot .free-input__input {
    @apply p-0;
  }

  .free-input__input {
    @apply text-text-neutral-primary w-full resize-none border-none
    px-0 py-2 outline-none;
    background-color: var(
      --input-background,
      rgb(var(--situational-lightgrey))
    );
  }

  .free-input__input:disabled {
    @apply text-text-neutral-quaternary cursor-not-allowed;
    -webkit-text-fill-color: theme('colors.text.neutral.quaternary');
    opacity: 1;
  }

  .input-fade-enter-active,
  .input-fade-leave-active {
    transition: opacity 0.15s;
  }

  .input-fade-enter-from,
  .input-fade-leave-to {
    opacity: 0;
  }
</style>
